/**
 * Pallas AI — Standalone Application
 *
 * Self-contained Pallas workspace with own navigation, header, and status bar.
 * No dependency on Ageleia Layout, Sidebar, or Header.
 */

import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { THEME } from './utils/theme';
import { fetchMeta } from './services/pallasService';
import { pallasKeyframes, MODULE_COLORS, Spinner } from './components/PallasAnalyst/PallasUI';
import { PallasNavigationRail, VALID_MODULES } from './components/PallasAnalyst/PallasNavigationRail';
import { PallasErrorBoundary } from './components/PallasAnalyst/PallasErrorBoundary';
import { usePallasThreads } from './hooks/usePallasThreads';
import useViewport from './hooks/useViewport';

// Module components (lazy loaded). EDR/NIDS/Correlations screens were retired
// in the v2.5 chat-first shell; their data is reached by asking in chat.
// `vulns` stays routable (by URL hash) but is hidden from the sidebar.
const PallasSOCChat = lazy(() => import('./components/PallasAnalyst/PallasSOCChat'));
const PallasVulnsDashboard = lazy(() => import('./components/PallasAnalyst/PallasVulnsDashboard'));
const PallasAttackNarrative = lazy(() => import('./components/PallasAnalyst/PallasAttackNarrative'));
const PallasStandupModule = lazy(() => import('./components/PallasAnalyst/PallasStandupModule'));

// ─── Helpers ────────────────────────────────────────────────────────────────

const DEFAULT_TOOL_HEALTH = {
  wazuh_manager: 'connecting',
  wazuh_indexer: 'connecting',
  suricata: 'connecting',
  ollama: 'connecting',
};

const getInitialModule = () => {
  // Prefer the ?view= query param: it survives the Keycloak SSO login redirect
  // (which rewrites the URL fragment), so deep-links from Ageleia land on the
  // right module even after a sign-in round-trip. Fall back to the #hash for
  // direct links and the embedded/plugin path.
  try {
    const view = new URLSearchParams(window.location.search).get('view');
    if (VALID_MODULES.includes(view)) return view;
  } catch (_) { /* URLSearchParams unavailable — fall through to hash */ }
  const hash = window.location.hash.slice(1);
  return VALID_MODULES.includes(hash) ? hash : 'chat';
};

// Embed chrome controls set by a hosting parent (Ageleia in-window Alert Triage:
// ?chrome=none&density=compact). Absent for standalone Pallas, the /ai/pallas
// embed, and the Wazuh dashboard plugin → they resolve to {false,false} = unchanged.
const getEmbedChrome = () => {
  try {
    const p = new URLSearchParams(window.location.search);
    return { hideChrome: p.get('chrome') === 'none', compact: p.get('density') === 'compact' };
  } catch (_) {
    return { hideChrome: false, compact: false };
  }
};

function ModuleLoadingFallback({ moduleId }) {
  const mc = MODULE_COLORS[moduleId] || MODULE_COLORS.chat;
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', height: '100%', gap: 12,
      animation: 'pallas-fadeIn 0.2s ease',
    }}>
      <Spinner size={20} color={mc.accent} />
      <span style={{ fontSize: 12, color: THEME.textMuted, fontWeight: 500 }}>
        Loading {mc.label}...
      </span>
    </div>
  );
}

// ─── Main App ───────────────────────────────────────────────────────────────

export default function App() {
  const [activeModule, setActiveModule] = useState(getInitialModule);
  // Guard booleans for the in-window embed. Both false on every existing path
  // (standalone, /ai/pallas, Wazuh plugin — none pass chrome/density).
  const { hideChrome, compact } = getEmbedChrome();
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [toolHealth, setToolHealth] = useState(DEFAULT_TOOL_HEALTH);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [pendingChatQuery, setPendingChatQuery] = useState(null);
  const [chatResetKey, setChatResetKey] = useState(0);

  // Below laptop width the 248px rail would squeeze the answer column, so it
  // becomes a drawer opened from a slim top bar.
  const { compact: compactViewport } = useViewport();
  const railAsDrawer = !hideChrome && compactViewport;
  const [railOpen, setRailOpen] = useState(false);
  useEffect(() => { if (!railAsDrawer) setRailOpen(false); }, [railAsDrawer]);
  useEffect(() => {
    if (!railOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setRailOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [railOpen]);
  // In drawer mode every rail action also closes the drawer.
  const closeRailAfter = useCallback((fn) => (...args) => {
    setRailOpen(false);
    return typeof fn === 'function' ? fn(...args) : undefined;
  }, []);

  // Persistent conversation threads (left-sidebar "Chats" list).
  const {
    threads,
    activeThreadId,
    activeThread,
    openThread,
    clearActive,
    deleteThread,
    commitTurn,
    // F2: backend sync indicator surfaced as a sidebar badge so analysts can
    // tell when the chat list is fresh from OpenSearch vs falling back to the
    // local cache (offline). The hook already maintains this state machine.
    backendStatus,
    // F4: chat-management affordances — rename (stored with the chat, so it
    // shows on every device) and a manual refresh trigger so analysts can
    // re-pull the list after deleting threads on another device.
    renameThread,
    refreshFromBackend,
  } = usePallasThreads();

  // Signed-in user for the sidebar footer (from the parent-dashboard iframe
  // handshake when present; falls back to a generic analyst identity).
  const user = (() => {
    const pa = (typeof window !== 'undefined' && window.__pallasParentAuth) || null;
    return {
      displayName: pa?.displayName || pa?.username || 'Analyst',
      role: 'SOC Analyst',
    };
  })();

  // ─── Hide preloader once app mounts ─────────────────────────────────────
  useEffect(() => {
    if (window.__hidePallasPreloader) window.__hidePallasPreloader();
  }, []);

  // ─── Tool health polling ──────────────────────────────────────────────────

  const fetchToolHealth = useCallback(async () => {
    try {
      const data = await fetchMeta();
      const svc = data.services || {};
      const mapStatus = (key) => {
        const val = svc[key];
        if (!val) return 'disabled';
        if (val === 'healthy') return 'connected';
        if (typeof val === 'string' && val.startsWith('unhealthy')) return 'error';
        return 'connecting';
      };
      setToolHealth({
        wazuh_manager: mapStatus('wazuh_manager'),
        wazuh_indexer: mapStatus('wazuh_indexer'),
        suricata: mapStatus('suricata_ids'),
        ollama: svc.query_orchestrator === 'initialized' ? 'connected' : 'disabled',
      });
      // Expose backend capability flags to components (read by PallasAttackNarrative
      // to gate Mute / Block UI). Stored on window so plain functions can read
      // synchronously without subscribing to a context.
      if (data && data.capabilities) {
        window.__pallasCapabilities = data.capabilities;
      }
      setConnectionStatus('connected');
    } catch {
      setConnectionStatus((prev) => (prev === 'connected' ? prev : 'error'));
    }
  }, []);

  useEffect(() => {
    fetchToolHealth();
    const interval = setInterval(fetchToolHealth, 30000);
    return () => clearInterval(interval);
  }, [fetchToolHealth]);

  useEffect(() => {
    if (connectionStatus === 'connected') {
      setToolHealth((prev) => {
        const hasRestData = prev.wazuh_manager === 'connected';
        if (hasRestData) return prev;
        return { wazuh_manager: 'connected', wazuh_indexer: 'connected', suricata: 'connected', ollama: 'connected' };
      });
    }
  }, [connectionStatus]);

  // ─── Ctrl+K command palette ─────────────────────────────────────────────

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen((prev) => !prev);
      }
      if (e.key === 'Escape') setCommandPaletteOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // ─── URL hash sync ────────────────────────────────────────────────────────

  useEffect(() => { window.location.hash = activeModule; }, [activeModule]);

  useEffect(() => {
    const onHashChange = () => {
      const hash = window.location.hash.slice(1);
      if (VALID_MODULES.includes(hash)) setActiveModule(hash);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // ─── Cross-module query completion ────────────────────────────────────────

  // Chat turns are committed to the thread store directly by the chat (it has
  // the parsed response). This stays for cross-module completion logging.
  const handleQueryComplete = useCallback((module, query, durationMs) => {
    if (module !== activeModule) {
      console.info(`[Pallas] ${module} query completed in ${(durationMs / 1000).toFixed(1)}s`);
    }
  }, [activeModule]);

  const handleModuleChange = useCallback((id) => {
    if (VALID_MODULES.includes(id)) setActiveModule(id);
  }, []);

  // The chat's own "New Chat" button calls this to keep the sidebar thread
  // store in sync — clear the active thread so the next query starts fresh.
  const handleChatNewChat = useCallback(() => {
    clearActive();
  }, [clearActive]);

  // Global top bar handlers — used by the App-level header so the same
  // buttons (Browse Quick Actions, Daily Standup, New Chat) work consistently
  // from any module, not just the chat shell.
  const handleGlobalNewChat = useCallback(() => {
    clearActive();
    setPendingChatQuery(null);
    setActiveModule('chat');
    setChatResetKey(k => k + 1);
  }, [clearActive]);

  // Open an existing thread from the sidebar — RESTORES its stored turns.
  // Remount (chatResetKey) clears any live response so only the thread's
  // transcript shows; no re-run.
  const handleOpenThread = useCallback((id) => {
    openThread(id);
    setPendingChatQuery(null);
    setActiveModule('chat');
    setChatResetKey(k => k + 1);
  }, [openThread]);

  // ─── Module renderer ──────────────────────────────────────────────────────

  const renderModule = () => {
    const commonProps = {
      connectionStatus,
      onConnectionStatusChange: setConnectionStatus,
      onQueryComplete: handleQueryComplete,
    };

    switch (activeModule) {
      case 'chat':
        return (
          <PallasSOCChat
            key={`chat-${chatResetKey}`}
            {...commonProps}
            commandPaletteOpen={commandPaletteOpen}
            onCommandPaletteClose={() => setCommandPaletteOpen(false)}
            pendingQuery={pendingChatQuery}
            onPendingQueryConsumed={() => setPendingChatQuery(null)}
            onNewChat={handleChatNewChat}
            activeThread={activeThread}
            activeThreadId={activeThreadId}
            onCommitTurn={commitTurn}
            onModuleChange={setActiveModule}
          />
        );
      case 'vulns': return <PallasVulnsDashboard {...commonProps} />;
      case 'narrative': return <PallasAttackNarrative {...commonProps} />;
      case 'standup': return <PallasStandupModule {...commonProps} />;
      default: return null;
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div style={{
      // Full-height shell — fills the viewport so the chat reads as one
      // unified surface with no bottom gap. (Earlier builds shrank this by 48px
      // as a stopgap for the over-constrained Wazuh plugin iframe; that left a
      // visible bottom strip in standalone mode. If Pallas is re-embedded in
      // that iframe and footers clip, reintroduce the `- 48px` offset.)
      height: '100vh',
      display: 'flex',
      flexDirection: 'row',
      backgroundColor: THEME.bg,
      overflow: 'hidden',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
      fontSize: compact ? '12px' : '13px',
      lineHeight: '1.5',
      color: THEME.text,
      WebkitFontSmoothing: 'antialiased',
    }}>
      <style>{pallasKeyframes()}</style>

      {/* Left sidebar — owns the action buttons (New Chat, Browse Quick
          Actions, Daily Standup) so the main column has zero chrome above
          the module content. Hidden for the Ageleia in-window embed
          (chrome=none): only Ageleia's left nav shows; the main column
          reclaims the width via flex:1. */}
      {!hideChrome && !railAsDrawer && (
        <PallasNavigationRail
          activeModule={activeModule}
          onModuleChange={handleModuleChange}
          toolHealth={toolHealth}
          user={user}
          threads={threads}
          activeThreadId={activeThreadId}
          onOpenThread={handleOpenThread}
          onDeleteThread={deleteThread}
          onNewChat={handleGlobalNewChat}
          onBrowseQuickActions={() => { setActiveModule('chat'); setCommandPaletteOpen(true); }}
          backendStatus={backendStatus}
          onRenameThread={renameThread}
          onRefreshThreads={refreshFromBackend}
        />
      )}

      {/* Narrow screens: the same rail as a slide-in drawer over the content. */}
      {railAsDrawer && railOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex' }}>
          <div style={{ height: '100%', boxShadow: '0 0 40px rgba(0,0,0,0.45)', animation: 'pallas-fadeIn 0.15s ease' }}>
            <PallasNavigationRail
              activeModule={activeModule}
              onModuleChange={closeRailAfter(handleModuleChange)}
              toolHealth={toolHealth}
              user={user}
              threads={threads}
              activeThreadId={activeThreadId}
              onOpenThread={closeRailAfter(handleOpenThread)}
              onDeleteThread={deleteThread}
              onNewChat={closeRailAfter(handleGlobalNewChat)}
              onBrowseQuickActions={closeRailAfter(() => { setActiveModule('chat'); setCommandPaletteOpen(true); })}
              backendStatus={backendStatus}
              onRenameThread={renameThread}
              onRefreshThreads={refreshFromBackend}
            />
          </div>
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setRailOpen(false)}
            style={{ flex: 1, border: 'none', background: 'rgba(0,0,0,0.5)', cursor: 'pointer', padding: 0 }}
          />
        </div>
      )}

      {/* Main column: module content (no top bar — actions live in the rail) */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
        {railAsDrawer && (
          <div style={{
            height: 44, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10,
            padding: '0 10px', borderBottom: `1px solid ${THEME.border}`, backgroundColor: THEME.railBg,
          }}>
            <button
              type="button"
              aria-label="Open menu"
              aria-expanded={railOpen}
              onClick={() => setRailOpen(true)}
              style={{
                width: 36, height: 36, display: 'grid', placeItems: 'center', borderRadius: 8,
                border: `1px solid ${THEME.border}`, background: 'transparent', color: THEME.text, cursor: 'pointer',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <path d="M4 7h16M4 12h16M4 17h16" />
              </svg>
            </button>
            <span style={{ fontWeight: 600, fontSize: 14 }}>Pallas</span>
            <span style={{ flex: 1 }} />
            <button
              type="button"
              onClick={handleGlobalNewChat}
              style={{
                height: 32, padding: '0 12px', borderRadius: 8, border: `1px solid ${THEME.border}`,
                background: 'transparent', color: THEME.text, fontSize: 12.5, cursor: 'pointer',
              }}
            >New chat</button>
          </div>
        )}
        <div className="pallas-scrollbar" style={{ flex: 1, overflow: 'hidden', position: 'relative', backgroundColor: THEME.bg }}>
          <PallasErrorBoundary>
            <Suspense fallback={<ModuleLoadingFallback moduleId={activeModule} />}>
              <div key={activeModule} style={{ animation: 'pallas-fadeIn 0.2s ease', height: '100%' }}>
                {renderModule()}
              </div>
            </Suspense>
          </PallasErrorBoundary>
        </div>
      </div>
    </div>
  );
}
